import React, { useEffect, useState } from 'react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';

const RELOAD_GUARD_KEY = 'mt_chunk_reload_at';
const GUARD_WINDOW_MS = 15000;

function isChunkError(error) {
  const name = error?.name || '';
  const message = error?.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(message) ||
    /dynamically imported module/i.test(message)
  );
}

/**
 * Fängt Routen-Fehler aller Lazy-Routen ab.
 *
 * Wichtig: Ein alter Tab lädt nach einem Deploy einen Chunk mit einem Hash,
 * den es nicht mehr gibt (404). Genau ein automatischer Reload holt die neue
 * Version - der sessionStorage-Riegel verhindert dabei eine Endlosschleife,
 * falls der Fehler eine andere Ursache hat.
 */
export default function RouteError() {
  const error = useRouteError();
  const [autoReloading, setAutoReloading] = useState(false);

  useEffect(() => {
    if (!isChunkError(error)) return;
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < GUARD_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    setAutoReloading(true);
    window.location.reload();
  }, [error]);

  if (autoReloading) {
    return (
      <div className="mt-loading">
        <div className="mt-spinner" />
      </div>
    );
  }

  // Ohne Fehlerobjekt sind wir über die Catch-all-Route hier gelandet, also
  // über eine Adresse, die es nicht gibt. Ein "Da ist etwas schiefgelaufen"
  // wäre bei einem simplen Tippfehler in der URL irreführend.
  const is404 = !error || (isRouteErrorResponse(error) && error.status === 404);

  return (
    <div className="mt-section">
      <div className="mt-container mt-narrow">
        <div className="mt-card mt-status">
          <div className="mt-status__icon" aria-hidden="true">
            {is404 ? '🧭' : '🔧'}
          </div>
          <h1 className="mt-status__title">
            {is404 ? 'Seite nicht gefunden' : 'Da ist etwas schiefgelaufen'}
          </h1>
          <p>
            {is404
              ? 'Diese Adresse gibt es bei uns nicht. Vielleicht hat sich ein Tippfehler eingeschlichen.'
              : 'Wahrscheinlich ist eine neue Version der Seite verfügbar. Ein Neuladen löst das meistens.'}
          </p>
          <div className="mt-row mt-status__actions">
            <Link to="/" className="mt-btn mt-btn--primary mt-btn--sm">
              Zur Startseite
            </Link>
            {!is404 && (
              <button
                type="button"
                className="mt-btn mt-btn--ghost mt-btn--sm"
                onClick={() => window.location.reload()}
              >
                Neu laden
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
